import { clamp01, TAU } from './math';
import type { Rng } from './rng';
import type { TextField } from './text/textField';

/**
 * Canvas-wide point distributions.
 *
 * Voronoi, Delaunay, scatter, DLA seeds and growth attractors all start from "give me
 * N points, distributed like *this*". Keeping the strategies in one place means they
 * behave identically across techniques, and a new distribution becomes available to
 * all of them at once.
 */
export type PointDistribution =
  | 'random'
  | 'poisson'
  | 'grid'
  | 'outline'
  | 'inside'
  | 'outside'
  | 'nearText'
  | 'ring';

export const POINT_DISTRIBUTION_OPTIONS: { value: PointDistribution; label: string }[] = [
  { value: 'random', label: 'Zufällig' },
  { value: 'poisson', label: 'Poisson' },
  { value: 'grid', label: 'Raster mit Jitter' },
  { value: 'outline', label: 'Textkontur' },
  { value: 'inside', label: 'Textinneres' },
  { value: 'outside', label: 'Nur ausserhalb' },
  { value: 'nearText', label: 'Dicht am Text' },
  { value: 'ring', label: 'Ring um den Text' },
];

export interface PointSourceOptions {
  field: TextField;
  width: number;
  height: number;
  rng: Rng;
  /** Ring distance for the `ring` distribution, and falloff scale for `nearText`. */
  distance?: number;
  /** Jitter for the `grid` distribution, as a fraction of the cell. */
  jitter?: number;
}

/** Returns a flat [x, y, ...] array of at most `count` points. */
export function generatePoints(
  count: number,
  distribution: PointDistribution,
  opts: PointSourceOptions,
): Float32Array {
  const { field, width, height, rng } = opts;
  const n = Math.max(0, Math.round(count));
  if (n === 0) return new Float32Array(0);

  switch (distribution) {
    case 'grid': {
      const jitter = opts.jitter ?? 0.4;
      const cols = Math.max(1, Math.round(Math.sqrt((n * width) / height)));
      const rows = Math.max(1, Math.ceil(n / cols));
      const out = new Float32Array(n * 2);
      for (let i = 0; i < n; i++) {
        const cx = i % cols;
        const cy = Math.floor(i / cols) % rows;
        out[i * 2] = ((cx + 0.5 + rng.signed(jitter)) * width) / cols;
        out[i * 2 + 1] = ((cy + 0.5 + rng.signed(jitter)) * height) / rows;
      }
      return out;
    }

    case 'poisson':
      return poissonPoints(n, width, height, rng);

    case 'outline': {
      const samples = field.sampleOutline(n);
      const out = new Float32Array(samples.length * 2);
      samples.forEach((s, i) => {
        out[i * 2] = s.x;
        out[i * 2 + 1] = s.y;
      });
      return out;
    }

    case 'inside':
      return field.samplePoints(n, 'poisson', rng);

    case 'outside': {
      const out = new Float32Array(n * 2);
      let written = 0;
      for (let attempt = 0; attempt < n * 60 && written < n; attempt++) {
        const x = rng.next() * width;
        const y = rng.next() * height;
        if (field.isInside(x, y)) continue;
        out[written * 2] = x;
        out[written * 2 + 1] = y;
        written++;
      }
      return out.subarray(0, written * 2);
    }

    case 'nearText': {
      // Rejection sampling against a density that falls off with distance from the
      // glyphs, so detail concentrates where the type is.
      const reach = Math.max(1, opts.distance ?? field.maxOutside * 0.35);
      const out = new Float32Array(n * 2);
      let written = 0;
      for (let attempt = 0; attempt < n * 80 && written < n; attempt++) {
        const x = rng.next() * width;
        const y = rng.next() * height;
        const t = clamp01(Math.abs(field.distance(x, y)) / reach);
        if (rng.next() > (1 - t) ** 2) continue;
        out[written * 2] = x;
        out[written * 2 + 1] = y;
        written++;
      }
      return out.subarray(0, written * 2);
    }

    case 'ring': {
      const target = opts.distance ?? 60;
      const samples = field.sampleOutline(n);
      const out = new Float32Array(samples.length * 2);
      samples.forEach((s, i) => {
        const d = target * (0.55 + rng.next() * 0.9);
        out[i * 2] = s.x + s.nx * d;
        out[i * 2 + 1] = s.y + s.ny * d;
      });
      return out;
    }

    default: {
      const out = new Float32Array(n * 2);
      for (let i = 0; i < n; i++) {
        out[i * 2] = rng.next() * width;
        out[i * 2 + 1] = rng.next() * height;
      }
      return out;
    }
  }
}

/** Bridson blue-noise over the whole canvas. */
export function poissonPoints(n: number, width: number, height: number, rng: Rng): Float32Array {
  const radius = Math.sqrt((width * height * 0.7) / Math.max(1, n));
  const cell = radius / Math.SQRT2;
  const cols = Math.ceil(width / cell) + 1;
  const rows = Math.ceil(height / cell) + 1;
  const grid = new Int32Array(cols * rows).fill(-1);
  const px: number[] = [];
  const py: number[] = [];
  const active: number[] = [];

  const fits = (x: number, y: number): boolean => {
    if (x < 0 || y < 0 || x >= width || y >= height) return false;
    const gx = (x / cell) | 0;
    const gy = (y / cell) | 0;
    for (let yy = Math.max(0, gy - 2); yy <= Math.min(rows - 1, gy + 2); yy++) {
      for (let xx = Math.max(0, gx - 2); xx <= Math.min(cols - 1, gx + 2); xx++) {
        const id = grid[yy * cols + xx];
        if (id < 0) continue;
        const dx = px[id] - x;
        const dy = py[id] - y;
        if (dx * dx + dy * dy < radius * radius) return false;
      }
    }
    return true;
  };
  const add = (x: number, y: number): void => {
    const id = px.length;
    px.push(x);
    py.push(y);
    grid[((y / cell) | 0) * cols + ((x / cell) | 0)] = id;
    active.push(id);
  };

  add(rng.next() * width, rng.next() * height);
  while (active.length && px.length < n) {
    const pick = rng.int(0, active.length - 1);
    const id = active[pick];
    let placed = false;
    for (let i = 0; i < 20; i++) {
      const a = rng.next() * TAU;
      const d = radius * (1 + rng.next());
      const x = px[id] + Math.cos(a) * d;
      const y = py[id] + Math.sin(a) * d;
      if (!fits(x, y)) continue;
      add(x, y);
      placed = true;
      break;
    }
    if (!placed) {
      active[pick] = active[active.length - 1];
      active.pop();
    }
  }

  const count = Math.min(n, px.length);
  const out = new Float32Array(count * 2);
  for (let i = 0; i < count; i++) {
    out[i * 2] = px[i];
    out[i * 2 + 1] = py[i];
  }
  return out;
}
